import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { AlertOctagon, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import InlineChat from "../components/InlineChat";

const KIND_LABELS = {
  termination: "Termination Request",
  replacement: "Replacement Request",
  complaint: "Complaint",
  safety: "Safety Concern",
  payment_issue: "Payment Issue",
  other: "Other",
};

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: "complaint",
    subject: "",
    description: "",
    related_hire_id: "",
    related_contract_id: "",
  });

  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject || !form.description) {
      toast.error("Please fill in subject and description");
      return;
    }
    try {
      await api.post("/reports", {
        ...form,
        related_hire_id: form.related_hire_id || null,
        related_contract_id: form.related_contract_id || null,
      });
      toast.success("Report submitted. KSB will follow up within 24 hours.");
      setOpen(false);
      setForm({ kind: "complaint", subject: "", description: "", related_hire_id: "", related_contract_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2">Reports Channel</div>
            <h1 className="font-display text-4xl font-bold tracking-tight">Raise a request or concern</h1>
            <p className="text-[#5B6478] mt-2 max-w-2xl">
              Use this channel for termination requests, replacements, complaints, safety issues or any concern.
              KSB reviews every submission within 24 hours.
            </p>
          </div>
          <Button data-testid="new-report-btn" onClick={() => setOpen(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white">
            <Plus className="w-4 h-4 mr-2" /> New Report
          </Button>
        </div>

        <div className="mt-10 space-y-4">
          {reports.length === 0 && (
            <div className="text-center py-16 border border-dashed border-[#E4E7F5] rounded-md text-[#5B6478]">
              <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <div>No reports filed yet. Everything looks good.</div>
            </div>
          )}
          {reports.map((r) => (
            <div key={r.id} className="bg-white border border-[#E4E7F5] rounded-md p-5" data-testid={`report-${r.id}`}>
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#EEF0FA] text-[#0F1E4B] hover:bg-[#EEF0FA]">{KIND_LABELS[r.kind]}</Badge>
                    <span className="font-display font-semibold">{r.subject}</span>
                  </div>
                  <p className="text-sm text-[#5B6478] mt-2 max-w-2xl">{r.description}</p>
                  <div className="text-xs text-[#5B6478] mt-3">
                    Filed by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
                <Badge className={`capitalize ${r.status === "resolved" ? "bg-[#1E3A8A]" : r.status === "in_progress" ? "bg-amber-100 text-amber-900 hover:bg-amber-100" : "bg-[#B91C1C]"}`}>
                  {r.status?.replace("_", " ")}
                </Badge>
              </div>
              {r.admin_notes && (
                <div className="mt-4 bg-[#EEF0FA] rounded-md p-3 text-sm">
                  <div className="label-eyebrow mb-1">KSB Notes</div>
                  <div>{r.admin_notes}</div>
                </div>
              )}
              {r.resolution && (
                <div className="mt-3 bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 rounded-md p-3 text-sm">
                  <div className="label-eyebrow mb-1 text-[#1E3A8A]">Resolution</div>
                  <div>{r.resolution}</div>
                </div>
              )}
              <InlineChat
                messages={r.messages || []}
                currentUserId={user?.id}
                locked={r.status === "resolved" || r.status === "dismissed"}
                testIdPrefix={`report-chat-${r.id}`}
                onSend={async (body) => {
                  try {
                    await api.post(`/reports/${r.id}/messages`, { body });
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">File a Report</DialogTitle>
            <DialogDescription>
              All reports are confidential and reviewed by KSB. Retaliation against a reporter is a zero-tolerance violation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-eyebrow">Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="report-kind" className="mt-2 border-[#E4E7F5]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Subject</Label>
              <Input data-testid="report-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 border-[#E4E7F5]" placeholder="Short summary" />
            </div>
            <div>
              <Label className="label-eyebrow">Describe the situation</Label>
              <Textarea data-testid="report-description" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 border-[#E4E7F5]" placeholder="Include dates, names on file, and what outcome you would like." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="report-submit" onClick={submit} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
